/*
Product Form — Subscription Bundle variant

Isolated copy of the ProductForm class from web-components-product.js,
registered under its own custom element tag so it never touches the
shared product-form behavior used elsewhere in the theme.

The only functional change from the original is the purchase_option
selector fix in initAddToCart() below (see comment at that line).
*/

class ProductFormSubscriptionBundle extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants || "[]");
      this.sellingPlanGroups = JSON.parse(this.dataset.sellingPlans || "[]");
      this.productOptions = JSON.parse(this.dataset["productOptions"]);
      this.defaultPurchaseOption = this.dataset.defaultPurchaseOption;
      this.subscriptionProduct = JSON.parse(
        this.dataset.subscriptionProduct || "null"
      );
      this.onetimePriceCents = this.dataset.onetimePrice
        ? parseInt(this.dataset.onetimePrice, 10)
        : null;
      this.subscribePriceCents = this.dataset.subscribePrice
        ? parseInt(this.dataset.subscribePrice, 10)
        : null;
      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
    } catch (e) {
      console.error("Invalid dataset", e);
      return;
    }

    this.initSelectedVariantFromUrl();
    this.initOptionListeners();
    this.initQuantityListeners();
    this.initAddToCart();

    // Ensure the default purchase option is actually selected in the DOM
    if (this.defaultPurchaseOption === "subscribe") {
      const subscribeInput = this.querySelector(
        'input[name^="purchase_option_"][value="subscribe"]'
      );
      if (subscribeInput) {
        subscribeInput.checked = true;
      }
    }

    // This will now see the correct purchase option and sync everything
    this.handleOptionChange();

    // Explicitly ensure hidden inputs and prices are correct on load
    this.updateHiddenInputs();
    this.updatePrices();

    // Defensive re-sync: on a genuine fresh page load, this first pass has
    // been observed to occasionally miss the just-applied default checked
    // state for .price-js (main price header) specifically, even though
    // everything else initializes correctly. Re-running once on the next
    // tick reliably corrects it without changing anything for repeat visits
    // where it was already right.
    setTimeout(() => {
      this.updateHiddenInputs();
      this.updatePrices();
    }, 0);
  }

  initAddToCart() {
    // Delegated on `this` for the same reason as initOptionListeners(): the
    // <form> is a Liquid-rendered child that may not exist yet when
    // connectedCallback runs, so binding directly to it (found via
    // querySelector at that moment) could silently attach nothing.
    this.addEventListener("submit", async (e) => {
      const form = e.target;
      if (form.tagName !== "FORM") return;
      const atcButton = form.querySelector("button[type='submit']");
      if (!atcButton) return;

      e.preventDefault();
      atcButton.disabled = true;
      atcButton.innerHTML = "Adding...";

      const variantInput = this.querySelector('input[name="id"]');
      const quantityInput = this.querySelector('input[name="quantity"]');
      const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
      const subscriptionIdInput = this.querySelector(
        'input[name="subscription_id"]'
      );
      const subscriptionSellingPlanInput = this.querySelector(
        'input[name="subscription_selling_plan"]'
      );
      // Fixed: the real radios are named purchase_option_{{ group }}, not
      // the literal "purchase_option" — matches the selector used
      // everywhere else in this file (updateHiddenInputs, updatePrices,
      // initOptionListeners, handleOptionChange).
      const purchaseOption = this.querySelector(
        'input[name^="purchase_option_"]:checked'
      )?.value;

      if (!variantInput || !quantityInput) return;

      const quantity = parseInt(quantityInput.value, 10);
      const items = [];

      items.push({
        id: variantInput.value,
        quantity: quantity,
        ...(sellingPlanInput?.value
          ? { selling_plan: sellingPlanInput.value }
          : {}),
      });

      if (
        purchaseOption === "subscribe" &&
        subscriptionIdInput?.value &&
        subscriptionSellingPlanInput?.value
      ) {
        items.push({
          id: subscriptionIdInput.value,
          quantity: quantity,
          selling_plan: subscriptionSellingPlanInput.value,
        });
      }

      try {
        for (const item of items) {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [item] }),
          });

          if (!response.ok)
            throw new Error(`Add to cart failed: ${response.statusText}`);
        }

        // TVSquared action tracking
        if (window.tvqTrackAction) {
          var productTitle = document.querySelector('h1')?.textContent?.trim() || '';
          var price = String((this.selectedVariant?.price || 0) / 100);

          window.tvqTrackAction('add_to_cart', {
            'rev': '0', 'prod': productTitle, 'id': String(variantInput.value), 'promo': ''
          });
          window.tvqTrackAction('checkout_started', {
            'rev': price, 'prod': productTitle, 'id': '', 'promo': ''
          });
          if (purchaseOption === "subscribe") {
            window.tvqTrackAction('subscription_signup', {
              'rev': price, 'prod': productTitle, 'id': String(variantInput.value), 'promo': ''
            });
          }
        }

        // The $100 "Subscribe & Save" price is enforced by the FENOMEM100
        // discount CODE (a BXGY discount keyed to the Feno Membership being
        // purchased as a subscription — see the comment in
        // main-product-subscription-bundle.liquid). Since it's a code, not
        // an automatic discount, it has to be silently applied via this
        // redirect rather than requiring the customer to type it in.
        if (purchaseOption === "subscribe") {
          window.location.href = "/discount/FENOMEM100?redirect=/checkout";
        } else {
          window.location.href = "/checkout";
        }
      } catch (err) {
        console.error("Error adding to cart", err);
        alert("Something went wrong while adding to cart. Please try again.");
        atcButton.disabled = false;
        atcButton.innerHTML = "Buy Now";
      }
    });
  }

  initQuantityListeners() {
    const quantityEl = this.querySelector(".quantity-js");
    if (!quantityEl) return;
    const plusBtn = this.querySelector(".quantity-plus-js");
    const minusBtn = this.querySelector(".quantity-minus-js");

    if (plusBtn) {
      plusBtn.addEventListener("click", () => {
        let quantity = parseInt(quantityEl.textContent.trim()) || 1;
        quantity++;
        quantityEl.textContent = quantity;
        this.updateQuantityInput(quantity);
      });
    }

    if (minusBtn) {
      minusBtn.addEventListener("click", () => {
        let quantity = parseInt(quantityEl.textContent.trim()) || 1;
        quantity = Math.max(1, quantity - 1);
        quantityEl.textContent = quantity;
        this.updateQuantityInput(quantity);
      });
    }
  }

  updateQuantityInput(quantity) {
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (quantityInput) quantityInput.value = quantity;
  }

  formatMoney(cents) {
    const amount = (cents || 0) / 100;
    const with2 = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const noDec = amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, with2)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, with2)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
  }

  updatePrices() {
    const onetimePriceEl = this.querySelector("#onetime-price");
    if (onetimePriceEl && this.selectedVariant) {
      onetimePriceEl.textContent = this.formatMoney(this.selectedVariant.price);
    }

    const subscriptionPriceEl = this.querySelector("#subscription-price");
    const subscriptionDiscountEl = this.querySelector("#subscription-discount");
    const subscriptionLabel = this.querySelector("#subscription-label");
    const planSelector = this.querySelector("#subscription-plan");

    const priceEls = document.querySelectorAll(".price-js");
    if (priceEls.length > 0) {
      const purchaseOption = this.querySelector(
        'input[name^="purchase_option_"]:checked'
      )?.value;

      let activePriceCents = this.selectedVariant.price;

      if (!this.subscriptionProduct && purchaseOption === "subscribe") {
        const selectedPlanId = parseInt(planSelector.value);
        const allocation = this.selectedVariant.selling_plan_allocations?.find(
          (a) => a.selling_plan_id == selectedPlanId
        );
        if (allocation) {
          activePriceCents = allocation.price_adjustments[0]?.price;
        }
      } else if (this.subscriptionProduct) {
        // Force-add-a-subscription case: the discounted "subscribe" price
        // isn't a native selling plan allocation, it comes from the
        // data-subscribe-price/data-onetime-price attributes the section
        // renders (see pdp-block-subscription-bundle.liquid for where those
        // numbers come from).
        if (purchaseOption === "subscribe" && this.subscribePriceCents != null) {
          activePriceCents = this.subscribePriceCents;
        } else if (this.onetimePriceCents != null) {
          activePriceCents = this.onetimePriceCents;
        }
      }

      const formattedPrice = this.formatMoney(activePriceCents);
      priceEls.forEach((el) => {
        el.textContent = formattedPrice;
      });
    }

    if (!planSelector || !subscriptionPriceEl) return;

    if (this.subscriptionProduct) {
      let subVariant = this.subscriptionProduct.variants[0];
      if (planSelector.value) {
        const matchingVariant = this.subscriptionProduct.variants.find(
          (variant) =>
            variant.selling_plan_allocations?.some(
              (allocation) => allocation.selling_plan_id == planSelector.value
            )
        );
        if (matchingVariant) subVariant = matchingVariant;
      }

      subscriptionPriceEl.innerHTML = `<s class="opacity-50">${this.formatMoney(subVariant.price)}</s> <span>${this.formatMoney(0)}</span>`;

      if (subscriptionLabel) {
        subscriptionLabel.textContent = "Subscribe";
      }
    } else if (this.selectedVariant?.selling_plan_allocations?.length > 0) {
      const availablePlans = this.selectedVariant.selling_plan_allocations.map(
        (alloc) => alloc.selling_plan_id
      );

      [...planSelector.options].forEach((opt) => {
        if (!availablePlans.includes(parseInt(opt.value))) {
          opt.style.display = "none";
        } else {
          opt.style.display = "";
        }
      });

      if (!availablePlans.includes(parseInt(planSelector.value))) {
        planSelector.value = availablePlans[0];
      }

      this.updateSubscriptionPlanDisplay();

      const selectedPlanId = parseInt(planSelector.value);
      const allocation = this.selectedVariant.selling_plan_allocations.find(
        (a) => a.selling_plan_id == selectedPlanId
      );

      if (allocation) {
        const fullPrice = this.selectedVariant.price / 100;
        const discountedPrice = allocation.price_adjustments[0]?.price / 100;
        const discountPercent = Math.round(
          (1 - discountedPrice / fullPrice) * 100
        );

        if (discountPercent > 0) {
          subscriptionPriceEl.innerHTML = `<s class="opacity-50">${this.formatMoney(fullPrice * 100)}</s> <span>${this.formatMoney(discountedPrice * 100)}</span>`;
          if (subscriptionLabel) {
            subscriptionLabel.textContent = `Subscribe & Save ${discountPercent}%`;
          }
        } else {
          subscriptionPriceEl.innerHTML = this.formatMoney(discountedPrice * 100);
          if (subscriptionLabel) {
            subscriptionLabel.textContent = `Subscribe`;
          }
        }
      }
    }
  }

  initSelectedVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = parseInt(urlParams.get("variant"), 10);
    if (variantId && this.variants.some((v) => v.id === variantId)) {
      this.selectedVariant = this.variants.find((v) => v.id === variantId);
    } else {
      this.selectedVariant =
        this.variants.find((v) => v.available) || this.variants[0];
      this.updateUrlWithVariant(this.selectedVariant.id);
    }
  }

  initOptionListeners() {
    // Delegated on `this` rather than bound directly to the radios/select:
    // connectedCallback can fire before these Liquid-rendered children
    // (nested several levels down in a block loop) have actually been
    // inserted into the DOM yet, which made direct querySelectorAll+
    // addEventListener binding silently attach to nothing. Delegation only
    // needs `this` to exist, so it's immune to that timing gap.
    this.addEventListener("change", (e) => {
      const target = e.target;

      if (target.matches('input[type="radio"][name^="option-"]')) {
        this.handleOptionChange();
      } else if (
        target.matches('input[name^="purchase_option_"]') ||
        target.matches("#subscription-plan")
      ) {
        this.updateHiddenInputs();
        this.updatePrices();
        this.updateSubscriptionPlanDisplay();
      }
    });
  }

  updateSubscriptionPlanDisplay() {
    const planSelector = this.querySelector("#subscription-plan");
    const planDisplay = this.querySelector("#subscription-plan-display");
    if (!planSelector || !planDisplay) return;

    const selectedOption = planSelector.options[planSelector.selectedIndex];
    if (selectedOption) {
      planDisplay.textContent = selectedOption.textContent;
    }
  }

  handleOptionChange() {
    const labelEl = this.querySelector(".variant-label-js");
    const selectedOptions = [];
    const optionGroups = [
      ...new Set(
        [...this.querySelectorAll('input[type="radio"][name^="option-"]')].map(
          (input) => input.name
        )
      ),
    ];

    optionGroups.forEach((groupName) => {
      const selectedInput = this.querySelector(
        `input[name="${groupName}"]:checked`
      );
      if (selectedInput) selectedOptions.push(selectedInput.value);
    });

    const matchingVariant = this.variants.find((variant) => {
      return variant.options.every((opt, idx) => opt === selectedOptions[idx]);
    });

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;
      this.updateUrlWithVariant(matchingVariant.id);
      this.updateActiveStates();

      const planSelector = this.querySelector("#subscription-plan");
      if (planSelector && !this.subscriptionProduct) {
        const availablePlans =
          this.selectedVariant.selling_plan_allocations.map(
            (alloc) => alloc.selling_plan_id
          );

        [...planSelector.options].forEach((opt) => {
          if (!availablePlans.includes(parseInt(opt.value))) {
            opt.style.display = "none";
          } else {
            opt.style.display = "";
          }
        });

        if (!availablePlans.includes(parseInt(planSelector.value))) {
          planSelector.value = availablePlans[0];
        }
      }
    }

    // Always sync hidden inputs and prices, even if variant did not change
    this.updateHiddenInputs();
    this.updatePrices();

    if (labelEl) {
      const colorOption = this.productOptions?.find(
        (option) => option.name === "Color"
      );

      if (!!colorOption) {
        labelEl.textContent = this.selectedVariant.title;
      }
    }
  }

  updateHiddenInputs() {
    const purchaseOption = this.querySelector(
      'input[name^="purchase_option_"]:checked'
    );

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = this.selectedVariant?.id || "";
    }

    const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    const subscriptionIdInput = this.querySelector(
      'input[name="subscription_id"]'
    );
    const subscriptionSellingPlanInput = this.querySelector(
      'input[name="subscription_selling_plan"]'
    );
    const planSelector = this.querySelector("#subscription-plan");

    if (purchaseOption?.value === "subscribe") {
      if (this.subscriptionProduct) {
        const selectedSellingPlanId = planSelector.value;
        const matchingVariant = this.subscriptionProduct.variants.find(
          (variant) =>
            variant.selling_plan_allocations?.some(
              (allocation) =>
                allocation.selling_plan_id == selectedSellingPlanId
            )
        );

        if (subscriptionIdInput && matchingVariant) {
          subscriptionIdInput.value = matchingVariant.id;
        } else if (subscriptionIdInput) {
          subscriptionIdInput.value = "";
        }

        if (subscriptionSellingPlanInput) {
          subscriptionSellingPlanInput.value = selectedSellingPlanId;
        }

        if (sellingPlanInput) {
          sellingPlanInput.value = "";
        }
      } else {
        if (planSelector && sellingPlanInput) {
          const selectedSellingPlanId = parseInt(planSelector.value);
          const allocation =
            this.selectedVariant.selling_plan_allocations?.find(
              (alloc) => alloc.selling_plan_id == selectedSellingPlanId
            );
          if (allocation) {
            sellingPlanInput.value = selectedSellingPlanId;
          } else {
            sellingPlanInput.value = "";
          }
        }

        if (subscriptionIdInput) {
          subscriptionIdInput.value = "";
        }
        if (subscriptionSellingPlanInput) {
          subscriptionSellingPlanInput.value = "";
        }
      }
    } else {
      if (sellingPlanInput) {
        sellingPlanInput.value = "";
      }
      if (subscriptionIdInput) {
        subscriptionIdInput.value = "";
      }
      if (subscriptionSellingPlanInput) {
        subscriptionSellingPlanInput.value = "";
      }
    }
  }

  updateActiveStates() {
    this.querySelectorAll(".option").forEach((el) =>
      el.classList.remove("active")
    );
    const optionGroups = [
      ...new Set(
        [...this.querySelectorAll('input[type="radio"][name^="option-"]')].map(
          (input) => input.name
        )
      ),
    ];
    optionGroups.forEach((groupName) => {
      const selectedInput = this.querySelector(
        `input[name="${groupName}"]:checked`
      );
      if (selectedInput)
        selectedInput.previousElementSibling?.classList.add("active");
    });
  }

  updateUrlWithVariant(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.pushState({}, "", url.toString());
  }
}

customElements.define(
  "product-form-subscription-bundle",
  ProductFormSubscriptionBundle
);
